import Phaser from "phaser";
import { getDebugCombatOptions } from "../debugCombat";
import { newRun } from "../state/runState";
import { OverlayManager } from "../ui/OverlayManager";
import { ScreenManager } from "../screens/ScreenManager";
import { OverworldState } from "../state/overworldState";
import { EncounterEnemyConfig } from "../state/encounterEnemies";
import { navLog } from "../debugNavigation";
import { ScreenKey } from "../screens/types";
import { RUN_CHANGED_EVENT, RUN_HUD_SCENE_KEY, RUN_QUIT_EVENT } from "./RunHudScene";
import { MAIN_MENU_SCENE_KEY } from "./MainMenuScene";

export const ENCOUNTER_SCENE_KEY = "ENCOUNTER";

type EncounterSceneData = {
  fromOverworld?: boolean;
  overworldState?: OverworldState;
  enemyConfig?: EncounterEnemyConfig;
  targetScreen?: ScreenKey;
};

export class EncounterScene extends Phaser.Scene {
  worldLayer!: Phaser.GameObjects.Container;
  uiLayer!: Phaser.GameObjects.Container;
  overlayLayer!: Phaser.GameObjects.Container;

  overlay!: OverlayManager;
  screenManager!: ScreenManager;
  private overworldState?: OverworldState;
  private unsubscribeQuit?: () => void;

  constructor() {
    super(ENCOUNTER_SCENE_KEY);
  }

  /**
   * EncounterScene is the dedicated "battle/encounter" game mode.
   * It owns combat UI, overlays, and encounter lifecycle only—no overworld
   * movement, collision, or camera responsibilities live here. Input for this
   * scene should be scoped to encounter controls, keeping overworld logic
   * isolated in OverworldScene.
   */
  create(data?: EncounterSceneData) {
    // Clear any previous overworld state to avoid leaking runs between sessions.
    this.overworldState = undefined;

    // Ensure persistent run HUD is active and on top.
    if (!this.scene.isActive(RUN_HUD_SCENE_KEY)) {
      this.scene.launch(RUN_HUD_SCENE_KEY);
    }
    this.scene.bringToTop(RUN_HUD_SCENE_KEY);

    this.worldLayer = this.add.container(0, 0);
    this.worldLayer.setDepth(0);

    this.uiLayer = this.add.container(0, 0);
    this.uiLayer.setDepth(100);

    this.overlayLayer = this.add.container(0, 0);
    this.overlayLayer.setDepth(1000);

    this.overlay = new OverlayManager(this, this.overlayLayer);
    this.screenManager = new ScreenManager(this, this.uiLayer);

    // Handle quit requests initiated from the HUD.
    const onQuit = () => {
      this.overlay.transition(() => {
        if (this.scene.isActive(RUN_HUD_SCENE_KEY)) {
          this.scene.stop(RUN_HUD_SCENE_KEY);
        }
        this.scene.start(MAIN_MENU_SCENE_KEY);
      });
    };
    this.game.events.on(RUN_QUIT_EVENT, onQuit);
    this.unsubscribeQuit = () => this.game.events.off(RUN_QUIT_EVENT, onQuit);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribeQuit?.());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.unsubscribeQuit?.());

    // Store overworld state if coming from overworld
    if (data?.fromOverworld && data.overworldState) {
      this.overworldState = data.overworldState;
      // Delay slightly to ensure HUD scene has finished creating and registered its event listener.
      this.time.delayedCall(0, () => {
        this.game.events.emit(RUN_CHANGED_EVENT, this.overworldState!.run);
      });
    }

    if (data?.targetScreen) {
      navLog("EncounterScene", "Navigating to requested screen.", {
        targetScreen: data.targetScreen,
      });
      this.overlay.transition(() => {
        this.screenManager.setScreen(data.targetScreen as ScreenKey, {
          run: this.overworldState?.run,
          overworldState: this.overworldState,
          enemyConfig: data.enemyConfig,
        });
      });
      return;
    }

    // If coming from overworld, launch encounter
    if (data?.fromOverworld && this.overworldState) {
      navLog("EncounterScene", "Entered ENCOUNTER from overworld; launching encounter screen.", {
        floor: this.overworldState.run.floor,
        isBossEncounter: Boolean(this.overworldState.run.isBossEncounter),
        enemyCount: data.enemyConfig?.enemies?.length ?? 0,
      });
      this.overlay.transition(() => {
        this.screenManager.setScreen("ENCOUNTER", {
          run: this.overworldState!.run,
          enemyConfig: data.enemyConfig,
          overworldState: this.overworldState,
        });
      });
      return;
    }

    const debugCombat = getDebugCombatOptions();
    if (debugCombat) {
      try {
        const run = newRun();
        if (debugCombat.floor !== undefined) {
          run.floor = debugCombat.floor;
        }
        if (debugCombat.playerSkills && debugCombat.playerSkills.length > 0) {
          // Reset to only requested skills at level 1
          run.player.skills = {};
          for (const skillId of debugCombat.playerSkills) {
            run.player.skills[skillId] = 1;
          }
        }

        // Delay slightly to ensure HUD scene has finished creating and registered its event listener.
        this.time.delayedCall(0, () => {
          this.game.events.emit(RUN_CHANGED_EVENT, run);
        });
        this.screenManager.setScreen("ENCOUNTER", {
          run,
          enemyConfig: debugCombat.enemyConfig,
        });
        return;
      } catch (err) {
        console.error("[EncounterScene] Error during debug combat setup:", err);
        throw err;
      }
    }

    // EncounterScene should not own the main menu flow anymore.
    // If we landed here without an overworld handoff or debug combat, go to main menu.
    this.scene.start(MAIN_MENU_SCENE_KEY);
  }

  /**
   * Get the overworld state if available (for returning to overworld after encounter).
   */
  getOverworldState(): OverworldState | undefined {
    return this.overworldState;
  }
}
